import { useState } from 'react';
import { Button, Flex, Spinner, Text, TextField } from '@radix-ui/themes';
import { Monitor, MousePointerClick, RefreshCw } from 'lucide-react';

export interface Shot {
  b64: string;
  w: number;
  h: number;
  at: number;
}

interface Props {
  shot: Shot | null;
  pending: boolean;
  auto: boolean;
  disabled: boolean;
  onRefresh: () => void;
  onToggleAuto: () => void;
  onClick: (fx: number, fy: number, right: boolean) => void;
  onType: (text: string) => void;
  onKeys: (combo: string) => void;
}

function ago(at: number): string {
  const s = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (s < 1) return 'только что';
  if (s < 60) return `${s} сек назад`;
  return `${Math.round(s / 60)} мин назад`;
}

const KEYS: Array<{ label: string; combo: string }> = [
  { label: '⏎', combo: 'return' },
  { label: 'esc', combo: 'esc' },
  { label: 'tab', combo: 'tab' },
  { label: '⌫', combo: 'backspace' },
  { label: '↑', combo: 'up' },
  { label: '↓', combo: 'down' },
  { label: '←', combo: 'left' },
  { label: '→', combo: 'right' },
  { label: '⌘C', combo: 'cmd+c' },
  { label: '⌘V', combo: 'cmd+v' },
  { label: '⌘⇥', combo: 'cmd+tab' },
];

/**
 * A still frame of the Mac's screen, and — in control mode — a pointer onto it: a tap
 * becomes a click at the same fraction of the real display, plus a text field and a row
 * of keys. Not a video stream: a JPEG on demand is a fraction of the bytes and enough to
 * drive a GUI. The empty state names the Screen Recording grant, which only ever degrades
 * the capture to wallpaper, never errors.
 */
export function Screen({ shot, pending, auto, disabled, onRefresh, onToggleAuto, onClick, onType, onKeys }: Props) {
  const [control, setControl] = useState(false);
  const [right, setRight] = useState(false);
  const [text, setText] = useState('');

  const handleImgClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!control) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const fx = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const fy = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    onClick(fx, fy, right);
    setRight(false);
  };

  return (
    <Flex direction="column" style={{ height: '100%', minHeight: 0 }}>
      <Flex align="center" justify="between" gap="2" px="3" py="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
        <Flex align="center" gap="2" style={{ minWidth: 0 }}>
          <Button size="1" variant="soft" color="gray" disabled={disabled || pending} onClick={onRefresh}>
            {pending ? <Spinner size="1" /> : <RefreshCw size={13} />}
            Обновить
          </Button>
          <Button size="1" variant={auto ? 'solid' : 'soft'} color={auto ? 'jade' : 'gray'} disabled={disabled} onClick={onToggleAuto}>
            Авто
          </Button>
          <Button size="1" variant={control ? 'solid' : 'soft'} color={control ? 'jade' : 'gray'} disabled={disabled} onClick={() => setControl((v) => !v)}>
            <MousePointerClick size={13} /> Управление
          </Button>
        </Flex>
        {shot && (
          <Text size="1" style={{ color: 'var(--app-hint)', whiteSpace: 'nowrap' }}>
            {ago(shot.at)}
          </Text>
        )}
      </Flex>

      {control && (
        <Flex direction="column" gap="2" px="3" py="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
          <Flex gap="2">
            <TextField.Root
              placeholder="Печатать на маке…"
              value={text}
              disabled={disabled}
              style={{ flex: 1 }}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && text) {
                  onType(text);
                  setText('');
                }
              }}
            />
            <Button size="1" variant="soft" color="jade" disabled={disabled || !text} onClick={() => { onType(text); setText(''); }}>
              Ввод
            </Button>
            <Button size="1" variant={right ? 'solid' : 'soft'} color={right ? 'amber' : 'gray'} disabled={disabled} onClick={() => setRight((v) => !v)}>
              ПКМ
            </Button>
          </Flex>
          <Flex gap="1" wrap="wrap">
            {KEYS.map((k) => (
              <Button key={k.combo} size="1" variant="soft" color="gray" disabled={disabled} onClick={() => onKeys(k.combo)}>
                {k.label}
              </Button>
            ))}
          </Flex>
        </Flex>
      )}

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#000' }}>
        {shot ? (
          <img
            src={`data:image/jpeg;base64,${shot.b64}`}
            alt="Экран мака"
            onClick={handleImgClick}
            style={{ display: 'block', width: '100%', height: 'auto', cursor: control ? 'crosshair' : 'default' }}
          />
        ) : (
          <Flex direction="column" align="center" gap="3" style={{ padding: 24, textAlign: 'center' }}>
            <Monitor size={40} color="var(--gray-8)" />
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              {disabled ? 'Мак не на связи' : 'Нажми «Обновить», чтобы увидеть экран'}
            </Text>
            <Text size="1" style={{ color: 'var(--gray-8)', maxWidth: 280 }}>
              Если экран окажется пустым (только обои) — маку нужно разрешение: Системные
              настройки → Конфиденциальность → Запись экрана. Подтвердить можно только на
              самом маке.
            </Text>
          </Flex>
        )}
      </div>
    </Flex>
  );
}
